import type { FormField, FormFieldRating } from '../../types';
import type { AnalyticsOption, AnalyticsQuestion, Observation, ObservedValue } from './normalize';
import { histogramBins, mean, median, quantile, sampleStdDev, type Histogram } from './stats';

/** Fields every summary carries. */
interface SummaryBase {
  fieldId: string;
  fieldType: FormField['type'];
  label: string;
  /** Respondents counted in this summary. */
  answered: number;
  /** Size of the filtered population. */
  eligible: number;
  /** Answers left out: a snapshot changed the value kind, or a text answer did not read as a number. */
  excluded: number;
  /** Layout: a chart card, a one-line row, or an entry in the section text list. */
  display: 'chart' | 'line' | 'text';
}

/** One category of a distribution. `share` is of respondents who answered. */
export interface DistributionItem {
  key: string;
  label: string;
  count: number;
  share: number;
}

/** Counts per category for choices, checkboxes, countries and short text with few values. */
export interface DistributionSummary extends SummaryBase {
  kind: 'distribution';
  view: 'donut' | 'bars';
  /** Author order (short lists, zeros shown) or count order (long lists, used options only). */
  order: 'author' | 'count';
  multi: boolean;
  source: 'choice' | 'boolean' | 'address' | 'text';
  items: DistributionItem[];
  /** Single-select long lists: respondents in options beyond the top 10. */
  rest?: { count: number; options: number };
  /** Multi-select: selections per respondent who answered. */
  selections?: { mean: number; min: number; max: number };
  /** Attached Other text in response order. */
  otherTexts: string[];
  /** Faces ratings: the median face. */
  medianKey?: string;
}

/** Scale rating labels drawn at the ends of the value axis. */
export interface ScaleInfo {
  lowLabel: string;
  highLabel: string;
  direction: FormFieldRating['direction'];
}

/** Numbers: a histogram and the stat row. */
export interface NumericSummary extends SummaryBase {
  kind: 'numeric';
  source: 'number' | 'text';
  /** Answer values in ascending order. */
  values: number[];
  n: number;
  mean: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  stdDev: number | null;
  q1: number | null;
  q3: number | null;
  histogram: Histogram;
  scale?: ScaleInfo;
}

/** Net Promoter Score breakdown. */
export interface NpsSummary extends SummaryBase {
  kind: 'nps';
  promoters: number;
  passives: number;
  detractors: number;
  /** Percent promoters minus percent detractors, -100 to 100. */
  score: number;
  /** Count per score 0 to 10. */
  distribution: Array<{ value: number; count: number }>;
}

/** Ranking questions: average rank and first-place count per option. */
export interface RankingSummary extends SummaryBase {
  kind: 'ranking';
  items: Array<{ key: string; label: string; averageRank: number; firstPlace: number }>;
}

/** Matrix questions: counts per row and column. */
export interface MatrixSummary extends SummaryBase {
  kind: 'matrix';
  columns: AnalyticsOption[];
  rows: Array<{ key: string; label: string; answered: number; cells: DistributionItem[] }>;
}

/** Dates binned by day, week or month, contiguous with zeros. */
export interface TimelineSummary extends SummaryBase {
  kind: 'timeline';
  granularity: 'day' | 'week' | 'month';
  bins: Array<{ start: string; count: number }>;
}

/** Questions shown only as an answered count. */
export interface CountSummary extends SummaryBase {
  kind: 'count';
}

/** One question's deterministic summary, chosen from its type. */
export type QuestionSummary = DistributionSummary | NumericSummary | NpsSummary | RankingSummary | MatrixSummary | TimelineSummary | CountSummary;

/** The card's lead value: the top option's share, or the median. */
export type LeadValue = { kind: 'share'; label: string; value: number } | { kind: 'number'; value: number };

/** Short choice lists keep author order and zero options. */
const SHORT_LIST = 7;
/** Donuts only for simple part-to-whole. */
const DONUT_MAX_OPTIONS = 3;
/** Long lists show this many options before grouping or expanding. */
export const TOP_OPTIONS = 10;
/** Share of text answers that must read as a number. */
const NUMBER_TEXT_SHARE = 0.9;
/** Few-distinct text: at most this many values... */
const FEW_DISTINCT_MAX = 10;
/** ...and no more than this share of answers distinct. */
const FEW_DISTINCT_SHARE = 0.5;
/** Text types that never chart. */
const TEXT_LIST_TYPES = new Set(['email', 'phone', 'free_form_explore']);
/** Same labels as the CSV export's checkbox column. */
const CHECKBOX_OPTIONS: AnalyticsOption[] = [{ key: 'true', label: 'Checked' }, { key: 'false', label: 'Not checked' }];
/** One unsigned number token; commas are digit-group separators. */
const NUMBER_TOKEN = /[0-9][0-9,]*(?:\.[0-9]+)?/g;
/** Days per granularity step before switching to a coarser one. */
const DAY_RANGE_MAX = 31;
const WEEK_RANGE_MAX = 182;
const DAY_MS = 86_400_000;

/** Answered values of one kind, with the response they came from. */
function answeredValues<K extends ObservedValue['kind']>(observations: readonly Observation[], kind: K): Array<{ responseId: string; value: Extract<ObservedValue, { kind: K }> }> {
  return observations.flatMap(entry => entry.status === 'answered' && entry.value.kind === kind
    ? [{ responseId: entry.responseId, value: entry.value as Extract<ObservedValue, { kind: K }> }] : []);
}

/** Base fields shared by every summary. */
function base(question: AnalyticsQuestion, observations: readonly Observation[], answered: number, extraExcluded = 0): Omit<SummaryBase, 'display'> {
  return {
    fieldId: question.id,
    fieldType: question.type,
    label: question.label,
    answered,
    eligible: observations.length,
    excluded: observations.filter(entry => entry.status === 'excluded').length + extraExcluded,
  };
}

/** Count summary for questions with no picture. */
function countSummary(question: AnalyticsQuestion, observations: readonly Observation[], display: 'line' | 'text', answered = observations.filter(entry => entry.status === 'answered').length): CountSummary {
  return { kind: 'count', display, ...base(question, observations, answered) };
}

/** Compare labels by code unit so ordering never depends on the viewer's locale. */
function compareLabels(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Build a distribution from per-respondent category keys. Short lists keep
 * author order with zeros; long lists sort by count, ties by option order then
 * label, and drop unused options.
 */
function distribution(
  question: AnalyticsQuestion,
  observations: readonly Observation[],
  categories: AnalyticsOption[],
  picks: string[][],
  config: { source: DistributionSummary['source']; multi: boolean; view?: DistributionSummary['view']; order?: DistributionSummary['order']; otherTexts?: string[]; group?: boolean },
): DistributionSummary {
  const answered = picks.length;
  const counted = categories.map((option, index) => ({ ...option, index, count: picks.filter(keys => keys.includes(option.key)).length }));
  const order = config.order ?? (categories.length <= SHORT_LIST ? 'author' : 'count');
  const sorted = order === 'author' ? counted : counted.filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count || a.index - b.index || compareLabels(a.label, b.label));
  const grouped = config.group && order === 'count' && sorted.length > TOP_OPTIONS;
  const items = (grouped ? sorted.slice(0, TOP_OPTIONS) : sorted)
    .map(item => ({ key: item.key, label: item.label, count: item.count, share: answered ? item.count / answered : 0 }));
  const rest = grouped ? sorted.slice(TOP_OPTIONS) : [];
  const signatures = new Set(picks.map(keys => [...keys].sort().join('\u0000')));
  const uniform = answered > 0 && signatures.size === 1;
  const sizes = picks.map(keys => keys.length);
  return {
    kind: 'distribution',
    display: uniform ? 'line' : 'chart',
    ...base(question, observations, answered),
    view: config.view ?? (!config.multi && config.source === 'choice' && categories.length <= DONUT_MAX_OPTIONS ? 'donut' : 'bars'),
    order,
    multi: config.multi,
    source: config.source,
    items: uniform ? items.filter(item => item.count > 0) : items,
    ...(grouped ? { rest: { count: rest.reduce((sum, item) => sum + item.count, 0), options: rest.length } } : {}),
    ...(config.multi && answered ? { selections: { mean: mean(sizes)!, min: Math.min(...sizes), max: Math.max(...sizes) } } : {}),
    otherTexts: config.otherTexts ?? [],
  };
}

/** Choice, image choice and faces summaries. */
function summarizeChoice(question: AnalyticsQuestion, observations: readonly Observation[]): DistributionSummary {
  const values = answeredValues(observations, 'choice').map(entry => entry.value);
  const field = question.field;
  const multi = question.type === 'choice_multi' || (field.type === 'image_choice' && field.selectionMode === 'multiple');
  const rated = 'rating' in field && field.rating !== undefined;
  const summary = distribution(question, observations, question.options, values.map(value => value.keys), {
    source: 'choice',
    multi,
    group: !multi,
    ...(multi ? { order: 'count' as const } : {}),
    ...(rated ? { view: 'bars' as const, order: 'author' as const } : {}),
    ...(question.type === 'image_choice' ? { view: 'bars' as const } : {}),
    otherTexts: values.flatMap(value => value.otherText ? [value.otherText] : []),
  });
  if (rated && field.type === 'choice_single' && field.rating?.preset === 'faces' && values.length) {
    // Median face: the first face whose cumulative share reaches half (percentile_disc).
    let cumulative = 0;
    const median = question.options.find(option => {
      cumulative += values.filter(value => value.keys.includes(option.key)).length;
      return cumulative >= values.length / 2;
    });
    if (median) summary.medianKey = median.key;
  }
  return summary;
}

/** Numeric stat row and histogram over answer values. */
function numericSummary(question: AnalyticsQuestion, observations: readonly Observation[], values: number[], source: NumericSummary['source'], extraExcluded = 0): NumericSummary {
  const sorted = [...values].sort((a, b) => a - b);
  const field = question.field;
  const rating = field.type === 'number' ? field.rating : undefined;
  let histogram = histogramBins(sorted);
  let scale: ScaleInfo | undefined;
  if (rating?.preset === 'scale' && field.type === 'number') {
    const step = field.step && field.step > 0 ? field.step : 1;
    const low = field.min ?? sorted[0] ?? 0;
    const high = field.max ?? sorted[sorted.length - 1] ?? low;
    const points: number[] = [];
    for (let unit = 0; low + unit * step <= high + 1e-9; unit++) points.push(Number((low + unit * step).toPrecision(12)));
    const bins = points.map(value => ({ x0: value, x1: value, count: sorted.filter(item => item === value).length }));
    histogram = { kind: 'values', bins: rating.direction === 'descending' ? bins.reverse() : bins };
    scale = { lowLabel: rating.lowLabel, highLabel: rating.highLabel, direction: rating.direction };
  }
  return {
    kind: 'numeric',
    display: sorted.length ? 'chart' : 'line',
    ...base(question, observations, sorted.length, extraExcluded),
    source,
    values: sorted,
    n: sorted.length,
    mean: mean(sorted),
    median: median(sorted),
    min: sorted.length ? sorted[0] : null,
    max: sorted.length ? sorted[sorted.length - 1] : null,
    stdDev: sampleStdDev(sorted),
    q1: quantile(sorted, 0.25),
    q3: quantile(sorted, 0.75),
    histogram,
    ...(scale ? { scale } : {}),
  };
}

/** NPS: promoters 9 to 10, passives 7 to 8, detractors 0 to 6. */
function npsSummary(question: AnalyticsQuestion, observations: readonly Observation[], values: number[]): NpsSummary {
  const promoters = values.filter(value => value >= 9).length;
  const passives = values.filter(value => value >= 7 && value < 9).length;
  const detractors = values.filter(value => value < 7).length;
  return {
    kind: 'nps',
    display: 'chart',
    ...base(question, observations, values.length),
    promoters,
    passives,
    detractors,
    score: values.length ? ((promoters - detractors) / values.length) * 100 : 0,
    distribution: Array.from({ length: 11 }, (_, value) => ({ value, count: values.filter(item => item === value).length })),
  };
}

/**
 * Read a text answer as a number when it holds exactly one unsigned number
 * token (commas removed). Ranges, several numbers, signs and unit suffixes are
 * rejected. Display-only digit parsing; it never drives conversation behavior.
 */
export function parseNumberText(text: string): number | null {
  const tokens = [...text.matchAll(NUMBER_TOKEN)];
  if (tokens.length !== 1) return null;
  const [token] = tokens;
  const start = token.index;
  const before = text[start - 1] ?? '';
  const after = text[start + token[0].length] ?? '';
  // A trailing comma is punctuation, not part of the number.
  const digits = token[0].replace(/,+$/, '');
  if (/[\p{L}+-]/u.test(before) || /\p{L}/u.test(after)) return null;
  const value = Number(digits.replace(/,/g, ''));
  return Number.isFinite(value) ? value : null;
}

/** Text case-fold key shared by distinct counts and grouping. */
function foldText(value: string): string {
  return value.normalize('NFC').toLocaleLowerCase();
}

/**
 * Short text: read as a number when 90% of answers parse; chart its values
 * when there are 10 or fewer distinct values covering at most half the
 * answers; otherwise list it.
 */
function summarizeText(question: AnalyticsQuestion, observations: readonly Observation[]): QuestionSummary {
  const texts = answeredValues(observations, 'text').map(entry => entry.value.value);
  if (question.type !== 'short_text' || !texts.length) return countSummary(question, observations, 'text');
  const parsed = texts.map(parseNumberText);
  const numbers = parsed.filter((value): value is number => value !== null);
  if (numbers.length >= NUMBER_TEXT_SHARE * texts.length) {
    return numericSummary(question, observations, numbers, 'text', texts.length - numbers.length);
  }
  const groups = new Map<string, Map<string, number>>();
  for (const text of texts) {
    const spellings = groups.get(foldText(text)) ?? new Map<string, number>();
    spellings.set(text, (spellings.get(text) ?? 0) + 1);
    groups.set(foldText(text), spellings);
  }
  if (groups.size > FEW_DISTINCT_MAX || groups.size > FEW_DISTINCT_SHARE * texts.length) return countSummary(question, observations, 'text');
  const categories = [...groups].map(([key, spellings]) => ({
    key,
    label: [...spellings].sort((a, b) => b[1] - a[1] || compareLabels(a[0], b[0]))[0][0],
  })).sort((a, b) => compareLabels(a.label, b.label));
  return distribution(question, observations, categories, texts.map(text => [foldText(text)]), { source: 'text', multi: false, view: 'bars', order: 'count' });
}

/** Ranking: average rank (1 is first) and first-place count, best average first. */
function summarizeRanking(question: AnalyticsQuestion, observations: readonly Observation[]): RankingSummary | CountSummary {
  const orders = answeredValues(observations, 'ranking').map(entry => entry.value.keys);
  if (!orders.length) return countSummary(question, observations, 'line');
  const items = question.options.map((option, index) => {
    const ranks = orders.flatMap(keys => keys.includes(option.key) ? [keys.indexOf(option.key) + 1] : []);
    return { key: option.key, label: option.label, index, averageRank: ranks.length ? mean(ranks)! : Number.POSITIVE_INFINITY, firstPlace: orders.filter(keys => keys[0] === option.key).length };
  }).filter(item => Number.isFinite(item.averageRank))
    .sort((a, b) => a.averageRank - b.averageRank || a.index - b.index)
    .map(({ index: _index, ...item }) => item);
  return { kind: 'ranking', display: 'chart', ...base(question, observations, orders.length), items };
}

/** Matrix: count and share per row and column. */
function summarizeMatrix(question: AnalyticsQuestion, observations: readonly Observation[]): MatrixSummary | CountSummary {
  const field = question.field;
  const answers = answeredValues(observations, 'matrix').map(entry => entry.value.selections);
  if (field.type !== 'matrix' || !answers.length) return countSummary(question, observations, 'line');
  const columns = field.matrix.columns.map(column => ({ key: column.id, label: column.label }));
  const rows = field.matrix.rows.map(row => {
    const picks = answers.flatMap(selections => selections[row.id]?.length ? [selections[row.id]] : []);
    return {
      key: row.id,
      label: row.label,
      answered: picks.length,
      cells: columns.map(column => {
        const count = picks.filter(keys => keys.includes(column.key)).length;
        return { key: column.key, label: column.label, count, share: picks.length ? count / picks.length : 0 };
      }),
    };
  });
  return { kind: 'matrix', display: 'chart', ...base(question, observations, answers.length), columns, rows };
}

/** UTC midnight of a `YYYY-MM-DD` date. */
function dayTime(value: string): number {
  return Date.UTC(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
}

/** `YYYY-MM-DD` for a UTC time. */
function isoDay(time: number): string {
  return new Date(time).toISOString().slice(0, 10);
}

/** Dates by day, ISO week (Monday start) or month, depending on the range. */
function summarizeTimeline(question: AnalyticsQuestion, observations: readonly Observation[]): TimelineSummary | CountSummary {
  const days = answeredValues(observations, 'date').map(entry => dayTime(entry.value.value)).filter(Number.isFinite);
  if (!days.length) return countSummary(question, observations, 'line');
  const first = Math.min(...days);
  const last = Math.max(...days);
  const span = (last - first) / DAY_MS;
  const granularity: TimelineSummary['granularity'] = span <= DAY_RANGE_MAX ? 'day' : span <= WEEK_RANGE_MAX ? 'week' : 'month';
  /** Start of the bin containing a day. */
  const binStart = (time: number): number => {
    const date = new Date(time);
    if (granularity === 'day') return time;
    if (granularity === 'week') return time - ((date.getUTCDay() + 6) % 7) * DAY_MS;
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
  };
  /** Start of the following bin. */
  const nextStart = (time: number): number => {
    const date = new Date(time);
    if (granularity === 'day') return time + DAY_MS;
    if (granularity === 'week') return time + 7 * DAY_MS;
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
  };
  const bins: TimelineSummary['bins'] = [];
  for (let start = binStart(first); start <= last; start = nextStart(start)) {
    const end = nextStart(start);
    bins.push({ start: isoDay(start), count: days.filter(day => day >= start && day < end).length });
  }
  return { kind: 'timeline', display: 'chart', ...base(question, observations, days.length), granularity, bins };
}

/** Countries of address answers by count, top 10 plus the rest. */
function summarizeAddress(question: AnalyticsQuestion, observations: readonly Observation[]): DistributionSummary {
  const countries = answeredValues(observations, 'address').map(entry => entry.value.country);
  const categories = [...new Set(countries)].sort(compareLabels).map(country => ({ key: country, label: country }));
  return distribution(question, observations, categories, countries.map(country => [country]), { source: 'address', multi: false, view: 'bars', order: 'count', group: true });
}

/**
 * Summarize one question from its observations (one per eligible response),
 * choosing the view from the field type, option count and answers.
 */
export function summarizeQuestion(question: AnalyticsQuestion, observations: readonly Observation[]): QuestionSummary {
  const answered = observations.filter(entry => entry.status === 'answered').length;
  if (question.type === 'short_text' || TEXT_LIST_TYPES.has(question.type)) return summarizeText(question, observations);
  if (!answered) return countSummary(question, observations, 'line', 0);
  switch (question.type) {
    case 'choice_single':
    case 'choice_multi':
    case 'image_choice':
      return summarizeChoice(question, observations);
    case 'checkbox': {
      const picks = answeredValues(observations, 'boolean').map(entry => [String(entry.value.value)]);
      return distribution(question, observations, CHECKBOX_OPTIONS, picks, { source: 'boolean', multi: false, view: 'donut', order: 'author' });
    }
    case 'number': {
      const values = answeredValues(observations, 'number').map(entry => entry.value.value);
      const field = question.field;
      return field.type === 'number' && field.rating?.preset === 'nps' ? npsSummary(question, observations, values) : numericSummary(question, observations, values, 'number');
    }
    case 'ranking':
      return summarizeRanking(question, observations);
    case 'matrix':
      return summarizeMatrix(question, observations);
    case 'date_time':
      return summarizeTimeline(question, observations);
    case 'address':
      return summarizeAddress(question, observations);
    default:
      return countSummary(question, observations, 'line');
  }
}

/** The card's lead value: the top option's share for distributions, the median for numbers. */
export function leadValue(summary: QuestionSummary): LeadValue | null {
  if (summary.kind === 'distribution' && summary.items.length) {
    const top = summary.items.reduce((best, item) => item.count > best.count ? item : best);
    return { kind: 'share', label: top.label, value: top.share };
  }
  if (summary.kind === 'numeric' && summary.median !== null) return { kind: 'number', value: summary.median };
  return null;
}
